import { useState, useEffect, FormEvent, RefObject, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import Input from '@gym-atoms/Input/Input';
import Dropdown, { DropdownChangeEvent, SelectItem } from '@gym-atoms/Dropdown/Dropdown';
import Button, { ButtonSizes, ButtonVariants } from '@gym-atoms/Button/Button';
import { useForm } from 'react-hook-form';
import { AlertType, createAlert } from '@gym-atoms/Alert/Alert';
import {
  GymChainAccessCategoryInput,
  SiteLocationAccessCategorySite,
  DeletableResponse,
  MembershipInfo,
  LambdaResponse,
  AddSiteLocationAccessCategoryInput
} from '@gym-src/API';
import { API } from 'aws-amplify';
import {
  addAccessCategory,
  addGymChainAccessCategory,
  modifyGymChainAccessCategory,
  removeSiteLocationAccessCategory
} from '@gym-graphql/mutations';
import { useAppDispatch, useAppSelector } from '@gym-redux/store';
import DialogBox, { DialogBoxVariants } from '@gym-atoms/Dialog/DialogBox';
import Text from '@gym-atoms/Text/Text';
import { GymChainAccessCategory, partialGymType } from '@gym-particles/types/models';
import { UserRole } from '@gym-particles/types/User';
import { getPartialGymsOfGymChain } from '@gym-particles/queries';
import Loader from '@gym-atoms/Loader/Loader';
import {
  getAllMembershipsForSiteLocation,
  getSiteLocationAccessCategories,
  isSiteLocationAccessCategoryLinked
} from '@gym-graphql/queries';
import { fetchGymChainAccessCategory } from '../../../redux/slices/gymChainAccessCategorySlice';

const GymChainAccessCategoryInfoTemplate = (props: GymChainAccessCategoryInfoTemplateProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { gymChainAccessCategoryId, chainId } = useParams<{
    gymChainAccessCategoryId: string;
    chainId: string;
  }>();
  const dispatch = useAppDispatch();
  const [failedDialogVisible, setFailedDialogVisible] = useState(false);
  const [gymsDropDownItems, setGymsDropDownItems] = useState<Array<SelectItem>>([]);
  const [membershipsDropDownItems, setMembershipsDropDownItems] = useState<Array<SelectItem>>([]);
  const isAccessCategoriesLoading = useAppSelector(
    (state) => state.accessCategory.isAccessCategoriesLoading
  );
  const [defaultSelectedGyms, setDefaultSelectedGyms] = useState<
    Array<SiteLocationAccessCategorySite>
  >([]);
  const [defaultSelectedMemberships, setDefaultSelectedMemberships] = useState<
    Array<SiteLocationAccessCategorySite>
  >([]);
  const [gymRemovableCheckLoading, setGymRemovableCheckLoading] = useState(false);
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);
  const userId = useAppSelector((state) => state.user.userId);
  const userRole = useAppSelector((state) => state.user.userRole);
  const [isInserted, setIsInserted] = useState(false);
  const [schedulesOnly, setSchedulesOnly] = useState(false);
  const [sortField, setSortField] = useState('createdDate');
  const [sortOrder, setSortOrder] = useState(-1);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(1000);
  const [searchTerm, setSearchTerm] = useState('');

  const getTranslationKey = (e: string): string => {
    return props.type === 'new' ? 'ADD_NEW_ACCESS_CATEGORY.' + e : 'MODIFY_ACCESS_CATEGORY.' + e;
  };

  type FormInputs = {
    id?: number;
    siteId?: number;
    name?: string;
    gender?: string;
    age?: number;
    schedulesOnly?: string;
    status?: string;
    gyms?: Array<string>;
    memberships?: Array<number>;
  };

  const defaultValues: FormInputs = {
    id: props.accessCategory?.id || 0,
    siteId: props.accessCategory?.siteId || 0,
    name: props.accessCategory?.name || '',
    gender: props.accessCategory?.gender || '',
    age: props.accessCategory?.age || 0,
    schedulesOnly: props.accessCategory?.schedulesOnly ? 'true' : 'false',
    status: props.accessCategory?.status || '',
    gyms: [],
    memberships: []
  };

  const genderDropDownItems = [
    {
      label: t(getTranslationKey('FORM_GENDER_DROPDOWN_MALE')),
      value: 'male'
    },
    {
      label: t(getTranslationKey('FORM_GENDER_DROPDOWN_FEMALE')),
      value: 'female'
    },
    {
      label: t(getTranslationKey('FORM_GENDER_DROPDOWN_NA')),
      value: 'n/a'
    }
  ];

  const inputFieldChangeHandler = (e: FormEvent<HTMLInputElement>) => {
    setValue(e.currentTarget.name as keyof GymChainAccessCategoryInput, e.currentTarget.value);
    setFormValues({ ...formValues, [e.currentTarget.name]: e.currentTarget.value });
  };

  const [formValues, setFormValues] = useState(defaultValues);

  const [tempGymSelectionValues, setTempGymSelectionValues] = useState<string[] | undefined>(
    formValues.gyms
  );
  const [tempMembershipSelectVal, setTempMembershipSelectVal] = useState<number[] | undefined>(
    formValues.memberships
  );

  const { register, handleSubmit, setValue, errors } = useForm<FormInputs>({
    defaultValues: defaultValues
  });

  const DeleteDialogFooter = () => {
    return (
      <div>
        {gymRemovableCheckLoading ? (
          <></>
        ) : (
          <Button
            label={t('COMMON.OK')}
            variant={ButtonVariants.basic}
            onClick={() => setIsDeleteDialogVisible(false)}
          />
        )}
      </div>
    );
  };
  const RemovableGymDialog = () => {
    return (
      <div>
        <DialogBox
          dialogVisible={isDeleteDialogVisible}
          variant={DialogBoxVariants.long}
          dialogDismissableMask={true}
          onHideCallback={() => setIsDeleteDialogVisible(false)}
          dialogFooter={<DeleteDialogFooter />}
          dialogClosable={false}
          dialogHeader={
            gymRemovableCheckLoading
              ? t('MEMBERSHIP_LIST.TABLE.CHECK_LINKED_GYMS')
              : t('MEMBERSHIP_LIST.TABLE.DELETE_MODAL_HEADER')
          }
          content={
            gymRemovableCheckLoading ? (
              <>
                <div className="mb-3">
                  <Loader shape="rectangle" width="full" />
                </div>
                <div className="mb-3">
                  <Loader shape="rectangle" width="full" />
                </div>
                <div className="mb-3">
                  <Loader shape="rectangle" width="half" />
                </div>
              </>
            ) : (
              <Text>{t('MEMBERSHIP_LIST.TABLE.DELETE_GYM_MODAL_CANNOT')}</Text>
            )
          }
        />
      </div>
    );
  };

  const checkRemovableMemberships = async (gyms: number[]) => {
    console.log('checking removable memberships');
    return true;
  };

  const checkRemovableGyms = async (gyms: number[]) => {
    setIsDeleteDialogVisible(true);
    setGymRemovableCheckLoading(true);

    const selectedSiteLocationAccessCategoryId = defaultSelectedGyms.find(
      (i) => i.siteLocationId === +gyms[0]
    )?.siteLocationAccessCategoryId;

    try {
      const response = await (API.graphql({
        query: isSiteLocationAccessCategoryLinked,
        variables: {
          id: selectedSiteLocationAccessCategoryId
        }
      }) as Promise<{
        data: { isSiteLocationAccessCategoryLinked: DeletableResponse[] };
      }>);

      setGymRemovableCheckLoading(false);
      if (
        response.data.isSiteLocationAccessCategoryLinked.length === 1 &&
        response.data.isSiteLocationAccessCategoryLinked[0].tableName ===
          'SiteLocationAccessCategory'
      ) {
        setIsDeleteDialogVisible(false);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.log(error);
      setFailedDialogVisible(true);
      setGymRemovableCheckLoading(false);
      setIsDeleteDialogVisible(false);
    }
  };

  const getGyms = () => {
    try {
      // get a list of all gyms for gym chain
      const response = (API.graphql({
        query: getPartialGymsOfGymChain,
        variables: {
          id: chainId,
          offset: 0,
          pageSize: 1000,
          sortField: 'gymName',
          sortOrder: 'ASC',
          userId: userRole === UserRole.SYSTEM_ADMIN ? 0 : userId
        }
      }) as Promise<{
        data: { getGymsOfGymChain: { items: partialGymType[] } };
      }>).then((e) => {
        const gymsDropDown: SelectItem[] = e.data.getGymsOfGymChain.items.map((gym) => {
          return { label: gym.gymName, value: String(gym.gymId) };
        });

        setGymsDropDownItems(gymsDropDown);
        props.type === 'modify' && getDefaultSelectedGyms();
      });
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const getGymMemberships = async () => {
    try {
      console.log('defaultMemberships', defaultSelectedMemberships);
      const locationMemberships = [];
      for (const gym of formValues.gyms) {
        console.log({ gym });
        const locationFilteredMembershipsResponse = await (API.graphql({
          query: getAllMembershipsForSiteLocation,
          variables: {
            siteLocationId: gym,
            filterMemberships: false
          }
        }) as Promise<{
          data: { getAllMembershipsForSiteLocation: MembershipInfo[] };
        }>);
        const locationFilteredMembershipDropDownItems =
          locationFilteredMembershipsResponse.data.getAllMembershipsForSiteLocation
            .map((data) => {
              return {
                label: `${data.siteLocationName} - ${data.membershipName} (${data.importedId}) • ${data.program} [${data.scheduleType}]`,
                value: data.siteLocationMembershipId
              };
            })
            .filter((item) => item !== undefined) || [];
        console.log({ locationFilteredMembershipDropDownItems });
        locationMemberships.push(...locationFilteredMembershipDropDownItems);
        //props.type === 'modify' && getDefaultSelectedGyms();
      }
      setMembershipsDropDownItems(locationMemberships);
      console.log({ formValues });
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const getDefaultSelectedGyms = async () => {
    console.log('defaultSelectedGyms', gymChainAccessCategoryId);
    try {
      const defaultGymsResponse = await (API.graphql({
        query: getSiteLocationAccessCategories,
        variables: {
          accessCategoryId: +gymChainAccessCategoryId
        }
      }) as Promise<{
        data: { getSiteLocationAccessCategories: SiteLocationAccessCategorySite[] };
      }>);
      console.log({ fetchedGyms: defaultGymsResponse.data });

      setDefaultSelectedGyms(
        [
          ...new Set(
            defaultGymsResponse.data.getSiteLocationAccessCategories.map(
              (item) => item.siteLocationId
            )
          )
        ].map((ts) =>
          defaultGymsResponse.data.getSiteLocationAccessCategories.find(
            (item) => item.siteLocationId === ts
          )
        )
      );
      setDefaultSelectedMemberships(defaultGymsResponse.data.getSiteLocationAccessCategories);
    } catch (error) {
      console.error('Failed to fetch selected gyms', error);
    }
  };

  useEffect(() => {
    getGyms();
    dispatch(
      fetchGymChainAccessCategory({
        siteId: props.accessCategory?.siteId || 0
      })
    );
  }, []);

  useMemo(async () => {
    await getGymMemberships();
  }, [formValues.gyms]);

  const gymsDropdownHandler = async (e: DropdownChangeEvent) => {
    console.log(e);
    const removedGyms = defaultSelectedGyms
      .map((y: SiteLocationAccessCategorySite) => y.siteLocationId)
      .filter((x: number) => !e.value.includes(String(x)));

    let response;

    if (removedGyms.length > 0) {
      if (tempGymSelectionValues && tempGymSelectionValues?.length > e.value.length) {
        response = await checkRemovableGyms(removedGyms);
      } else {
        response = true;
      }
    } else {
      response = true;
    }
    if (response) {
      setValue('gyms', e.value);
      setFormValues({ ...formValues, gyms: e.value });
    } else {
      return;
    }
    await getGymMemberships();
    setTempGymSelectionValues(e.value);
  };

  const membershipsDropdownHandler = async (e: DropdownChangeEvent) => {
    const removedMemberships = defaultSelectedMemberships
      .map((y: SiteLocationAccessCategorySite) => y.membershipId)
      .filter((x: number) => !e.value.includes(String(x)));

    let response;

    if (removedMemberships.length > 0) {
      if (tempMembershipSelectVal && tempMembershipSelectVal?.length > e.value.length) {
        response = await checkRemovableMemberships(removedMemberships);
      } else {
        response = true;
      }
    } else {
      response = true;
    }
    if (response) {
      setValue('memberships', e.value);
      setFormValues({ ...formValues, memberships: e.value });
    } else {
      return;
    }
    setTempMembershipSelectVal(e.value);
  };

  useEffect(() => {
    setFormValues({
      ...formValues,
      id: props.accessCategory?.id || 0,
      siteId: props.accessCategory?.siteId || 0,
      name: props.accessCategory?.name || '',
      gender: props.accessCategory?.gender || '',
      schedulesOnly: props.accessCategory?.schedulesOnly ? 'true' : 'false',
      status: props.accessCategory?.status || '',
      gyms: defaultSelectedGyms.map((gym) => String(gym.siteLocationId)),
      memberships: defaultSelectedMemberships.map(
        (membership) => membership.siteLocationMembershipId
      )
    });

    console.log({ defaultSelectedGyms });
    console.log({ defaultSelectedMemberships });

    setValue('name', props.accessCategory?.name);
    setValue('siteId', props.accessCategory?.siteId);
    setValue('gender', props.accessCategory?.gender);
    setValue('age', props.accessCategory?.age);
    setValue('schedulesOnly', props.accessCategory?.schedulesOnly);
    setValue('status', props.accessCategory?.status);
    setValue(
      'gyms',
      defaultSelectedGyms.map((gym) => String(gym.siteLocationId))
    );
    setValue(
      'memberships',
      defaultSelectedMemberships.map((membership) => membership.siteLocationMembershipId)
    );

    setTempGymSelectionValues(defaultSelectedGyms.map((gym) => String(gym.siteLocationId)));
    setTempMembershipSelectVal(
      defaultSelectedMemberships.map((membership) => membership.siteLocationMembershipId)
    );
  }, [props, defaultSelectedGyms, defaultSelectedMemberships]);

  const ActionFailedDialog = () => {
    return (
      <div>
        <DialogBox
          variant={DialogBoxVariants.basic}
          dialogVisible={failedDialogVisible}
          onHideCallback={() => setFailedDialogVisible(false)}
          dialogHeader={t(getTranslationKey('FORM_FAILURE_DIALOG_HEADER'))}
          dialogFooter={
            <Button
              label={t(getTranslationKey('FORM_FAILURE_DIALOG_BUTTON'))}
              onClick={() => setFailedDialogVisible(false)}
            />
          }
          dialogDismissableMask={true}
          dialogClosable={false}
          content={<Text>{t(getTranslationKey('FORM_FAILURE_DIALOG_BODY'))}</Text>}
        />
      </div>
    );
  };

  const removeLocationAccessCategory = async (accessCategoryId: number) => {
    try {
      await API.graphql({
        query: removeSiteLocationAccessCategory,
        variables: {
          id: accessCategoryId
        }
      });
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const addSiteLocationAccessCategory = async (
    memberships: number[],
    addAccessCategoryResponse: GymChainAccessCategory
  ) => {
    try {
      console.log({ memberships });
      console.log({ addAccessCategoryResponse });
      const addSiteLocationAccessCategoryInput: AddSiteLocationAccessCategoryInput = {
        name: addAccessCategoryResponse.name.replace(/'/g, "''"),
        siteLocationMembershipIds: Array.isArray(memberships)
          ? memberships.map((id) => +id)
          : [memberships],
        gender: addAccessCategoryResponse.gender,
        age: addAccessCategoryResponse.age,
        accessCategoryId: addAccessCategoryResponse.id,
        arxAccessCategoryId: '',
        status: addAccessCategoryResponse.status,
        createdBy: userId,
        lastModifiedBy: userId,
        schedulesOnly: schedulesOnly,
        siteLocationMemberships: []
      };

      addSiteLocationAccessCategoryInput.siteLocationMemberships = addSiteLocationAccessCategoryInput.siteLocationMembershipIds.map(
        (id) => membershipsDropDownItems.find((m) => m.value === id)?.label || ''
      );
      console.log({ addSiteLocationAccessCategoryInput });

      try {
        const result = await (API.graphql({
          query: addAccessCategory,
          variables: {
            input: addSiteLocationAccessCategoryInput
          }
        }) as Promise<{
          data: { addAccessCategory: LambdaResponse };
        }>);
        if (result.data.addAccessCategory.error) {
          throw result.data.addAccessCategory;
        }
        console.log(result);
      } catch (error) {
        setFailedDialogVisible(true);
        console.log(error);
      }
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const addSiteAccessCategory = async (data: FormInputs) => {
    console.log('addAccessCategory:', data);
    const addAccessCategoryInput: GymChainAccessCategoryInput = {
      siteId: +chainId || 0,
      name: data.name || '',
      gender: data.gender || '',
      age: +data.age || 0,
      schedulesOnly: false,
      status: data.status || '',
      createdBy: userId,
      lastModifiedBy: userId
    };
    console.log('addAccessCategory data.gyms', data.gyms);
    console.log({ addAccessCategoryInput });

    try {
      const addAccessCategoryResponse = await (API.graphql({
        query: addGymChainAccessCategory,
        variables: {
          input: addAccessCategoryInput
        }
      }) as Promise<{
        data: { addGymChainAccessCategory: GymChainAccessCategory };
      }>);
      console.log({ addAccessCategoryResponse });
      console.log(data.memberships);

      if (data.memberships.length > 0) {
        await addSiteLocationAccessCategory(
          data.memberships,
          addAccessCategoryResponse.data.addGymChainAccessCategory
        );
      }
      createAlert(props.alertRef, {
        content: t(getTranslationKey('ALERT_MESSAGE_SUCCESS')),
        severity: 'success'
      });
      setTimeout(() => history.push(`/gymChains/${chainId}/gymChainAccessCategories`), 3000);
    } catch (e) {
      setFailedDialogVisible(true);
      console.log(e);
    }
  };

  const modifyAccessCategory = async (data: FormInputs) => {
    console.log('modify', { data });
    const modifyGymChainAccessCategoryInput: GymChainAccessCategoryInput = {
      id: props.accessCategory.id,
      siteId: props.accessCategory.siteId || 0,
      name: data.name || '',
      gender: data.gender || '',
      age: data.age || 0,
      schedulesOnly: schedulesOnly,
      status: data.status || '',
      createdBy: userId,
      lastModifiedBy: userId
    };

    try {
      const modifyGymAccessCategoryResponse = await (API.graphql({
        query: modifyGymChainAccessCategory,
        variables: {
          input: modifyGymChainAccessCategoryInput
        }
      }) as Promise<{
        data: { modifyGymChainAccessCategory: GymChainAccessCategory };
      }>);
      console.log({ modifyGymAccessCategoryResponse });

      const addedGyms = data.gyms?.filter(
        (x: string) =>
          !defaultSelectedGyms
            .map((y: SiteLocationAccessCategorySite) => y.siteLocationId)
            .includes(+x)
      );
      const addedMemberships = data.memberships?.filter(
        (x: number) =>
          !defaultSelectedMemberships
            .map((y: SiteLocationAccessCategorySite) => y.siteLocationMembershipId)
            .includes(+x)
      );
      console.log({ addedGyms });
      console.log({ addedMemberships });

      if (addedMemberships.length > 0) {
        await addSiteLocationAccessCategory(
          addedMemberships,
          modifyGymAccessCategoryResponse.data.modifyGymChainAccessCategory
        );
      }

      const removedMemberships = defaultSelectedMemberships
        .map((y: SiteLocationAccessCategorySite) => y.siteLocationMembershipId)
        .filter((x: number) => !data.memberships?.includes(Number(x)));
      console.log({ removedMemberships });
      removedMemberships?.forEach(async (g: number) => {
        const membershipRemoved = defaultSelectedMemberships.find(
          (i: SiteLocationAccessCategorySite) => i.siteLocationMembershipId === g
        )?.siteLocationAccessCategoryId;
        console.log({ membershipRemoved });

        if (membershipRemoved > 0) {
          await removeLocationAccessCategory(membershipRemoved || 0);
        }
      });

      createAlert(props.alertRef, {
        content: t(getTranslationKey('ALERT_MESSAGE_SUCCESS')),
        severity: 'success'
      });
      setTimeout(() => history.push(`/gymChains/${chainId}/gymChainAccessCategories`), 3000);
    } catch (e) {
      setFailedDialogVisible(true);
      console.log(e);
    }
  };

  //Form submission handler
  const onSubmitHandler = async (data: FormInputs) => {
    setIsInserted(true);
    if (props.type === 'new') {
      await addSiteAccessCategory(data);
    } else if (props.type === 'modify' && props.accessCategory) {
      await modifyAccessCategory(data);
    }
    setIsInserted(false);
  };

  useEffect(() => {
    register('name', {
      required: t(getTranslationKey('FORM_REQUIRED_FIELD_ERROR')) as string,
      maxLength: { value: 255, message: 'required' as string }
    });
    register('gender');
    register('age');
    register('schedulesOnly');
    register('status', {
      required: t(getTranslationKey('FORM_REQUIRED_FIELD_ERROR')) as string
    });
    register('gyms');
    register('memberships');
  }, [register]);

  // TO-DO [Suggestion] - Keep these values in one place as enums.

  const dropdownChangeHandler = (e: DropdownChangeEvent) => {
    setValue(e.target.name as keyof GymChainAccessCategoryInput, e.value);
    setFormValues({ ...formValues, [e.target.name]: e.value });
  };
  const statusDropDownItems = [
    {
      label: t(getTranslationKey('FORM_STATUS_DROPDOWN_ACTIVE')),
      value: 'active'
    },
    { label: t(getTranslationKey('FORM_STATUS_DROPDOWN_INACTIVE')), value: 'inactive' }
  ];
  const statusDropdownHandler = (e: DropdownChangeEvent) => {
    setValue('status', e.value);
    setFormValues({ ...formValues, status: e.value });
  };

  const changeHandler = (e: FormEvent<HTMLInputElement>) => {
    setValue(e.currentTarget.name as keyof FormInputs, e.currentTarget.value);
    setFormValues({ ...formValues, [e.currentTarget.name]: e.currentTarget.value });
    console.log({ formValues });
  };
  const loaderElement = () => {
    return (
      <div className="loaderHeader">
        <div className="loaderContent">
          <Loader shape="rectangle" width="quarter" />
        </div>
        <div>
          <Loader shape="rectangle" width="full" />
        </div>
      </div>
    );
  };
  return (
    <div>
      {isAccessCategoriesLoading ? (
        [...Array(5)].map((element, index) => {
          return <div key={index}> {loaderElement()} </div>;
        })
      ) : (
        <form className="input-mb20" onSubmit={handleSubmit(onSubmitHandler)} noValidate>
          <div className="row">
            <div className="col-12">
              <Input
                label={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_NAME_LABEL'))}
                variant="basic"
                name="name"
                value={formValues.name}
                onChange={inputFieldChangeHandler}
                error={errors.name ? true : false}
                errorMessage={errors.name?.message}
                placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_NAME_PLACEHOLDER'))}
                required={true}
              />
            </div>
          </div>
          <div className="col-12">
            <label className="fw-bold">
              {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_GENDER_LABEL'))}
            </label>
            <Dropdown
              name="gender"
              options={genderDropDownItems}
              value={formValues.gender}
              onChange={dropdownChangeHandler}
              placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_GENDER_PLACEHOLDER'))}
              error={errors.gender ? true : false}
            />
          </div>
          <div className="row">
            <div className="col-12">
              <Input
                label={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_AGE_LABEL'))}
                variant="basic"
                name="age"
                value={formValues.age > 0 ? formValues.age.toString() : ''}
                onChange={inputFieldChangeHandler}
                error={errors.age ? true : false}
                errorMessage={errors.age?.message}
                placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_AGE_PLACEHOLDER'))}
                required={false}
              />
            </div>
          </div>
          <div className="col-12">
            <label className="fw-bold">
              {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_STATUS_LABEL'))}
              <span className="p-error"> *</span>
            </label>
            <Dropdown
              name="status"
              options={statusDropDownItems}
              value={formValues.status}
              onChange={statusDropdownHandler}
              placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_STATUS_PLACEHOLDER'))}
              error={errors.status ? true : false}
            />
          </div>
          <div className="mb25">
            <label className="fw-bold">{t(getTranslationKey('FORM_INPUT_LABEL_GYMS'))}</label>
            <Dropdown
              key={gymsDropDownItems.length > 0 ? 'loaded' : 'loading'}
              chips={true}
              name="gyms"
              placeholder={t(getTranslationKey('FORM_INPUT_PLACEHOLDER_GYMS'))}
              value={formValues.gyms}
              options={gymsDropDownItems}
              onChange={gymsDropdownHandler}
              error={errors.gyms ? true : false}
            />
            {errors.gyms && (
              <p data-cy={'membership-info-temp-form-dropdown-gyms-error'} className="p-error">
                {t(getTranslationKey('FORM_GYMS_EMPTY_ERROR'))}
              </p>
            )}
          </div>
          {formValues.gyms.length > 0 && gymsDropDownItems.length > 0 && (
            <div className="mb25">
              <h4>Selected Gyms:</h4>
              <h3>{formValues.gyms?.length} Gyms selected</h3>
              <div>
                {formValues.gyms?.map((name, index) => (
                  <span
                    key={index}
                    className="p-badge"
                    style={{ color: 'white', fontWeight: 'bold', marginRight: '6px' }}>
                    {gymsDropDownItems.find((obj) => obj.value === name).label}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mb25">
            <label className="fw-bold">{t(getTranslationKey('FORM_INPUT_LABEL_MEMBERSHIP'))}</label>
            <Dropdown
              key={membershipsDropDownItems.length > 0 ? 'loaded' : 'loading'}
              chips={true}
              name="memberships"
              placeholder={t(getTranslationKey('FORM_INPUT_PLACEHOLDER_MEMBERSHIP'))}
              value={formValues.memberships}
              options={membershipsDropDownItems}
              onChange={membershipsDropdownHandler}
              error={errors.memberships ? true : false}
              disabled={membershipsDropDownItems.length === 0}
            />
            {errors.memberships && (
              <p
                data-cy={'membership-info-temp-form-dropdown-memberships-error'}
                className="p-error">
                {t(getTranslationKey('FORM_MEMBERSHIPS_EMPTY_ERROR'))}
              </p>
            )}
          </div>
          {formValues.memberships.length > 0 && membershipsDropDownItems.length > 0 && (
            <div className="mb25">
              <h4>Selected Memberships:</h4>
              <h3>{formValues.memberships?.length} Memberships selected</h3>
              <div>
                {formValues.memberships?.map((name, index) => (
                  <span
                    key={index}
                    className="p-badge"
                    style={{ color: 'white', fontWeight: 'bold', marginRight: '6px' }}>
                    {membershipsDropDownItems.find((obj) => obj.value === name).label}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="d-flex flex-row mt35 btn-min-w-110">
            <div className="mr15">
              <Button
                data-cy={'membership-info-temp-form-btn-save'}
                icon={isInserted ? 'pi-spinner pi-spin' : ''}
                label={t(getTranslationKey('FORM_BTN_LABEL_SAVE'))}
                size={ButtonSizes.medium}
                disabled={isInserted}
              />
            </div>
            <Button
              data-cy={'membership-info-temp-form-btn-cancel'}
              label={t(getTranslationKey('FORM_BTN_LABEL_CANCEL'))}
              variant={ButtonVariants.textonly}
              onClick={() => history.push(`/gymChains/${chainId}/gymChainAccessCategories`)}
              disabled={isInserted}
            />
            <ActionFailedDialog />
            <RemovableGymDialog />
          </div>
        </form>
      )}
    </div>
  );
};

export default GymChainAccessCategoryInfoTemplate;

type GymChainAccessCategoryInfoTemplateType = 'new' | 'modify';

export interface GymChainAccessCategoryInfoTemplateProps {
  type: GymChainAccessCategoryInfoTemplateType;
  accessCategory?: GymChainAccessCategory;
  alertRef: RefObject<AlertType>;
  alertMessage: string;
}
